import type { RentCreditOption } from '../../../helpers/utils/types';
import { findBestValueRentCreditId, formatPoints } from '../utils/redeemUtils';

type RedeemRentCreditsSectionProps = {
  loading: boolean;
  options: RentCreditOption[];
  pointsAvailable: number;
  redeemingId: string | null;
  onRedeem: (option: RentCreditOption) => Promise<void>;
};

export const RedeemRentCreditsSection = ({
  loading,
  options,
  pointsAvailable,
  redeemingId,
  onRedeem,
}: RedeemRentCreditsSectionProps) => {
  const bestValueId = findBestValueRentCreditId(options);

  return (
    <section id='rd-rent-savings'>
      <div className='rd-sec'>
        <div>
          <h2 className='rd-sec-title'>Redeem rent savings</h2>
          <p className='rd-sec-sub'>Use points for direct rent savings.</p>
        </div>
      </div>

      <div className='rd-rc-grid'>
        {loading ? (
          <div className='rd-rc-empty'>Loading rent credit options...</div>
        ) : options.length === 0 ? (
          <div className='rd-rc-empty'>No rent credit options available at the moment.</div>
        ) : (
          options.map((option) => {
            const optionPoints = Number(option.points);
            const eligible = optionPoints <= pointsAvailable;
            const isRedeeming = redeemingId === option.rentCreditOptionId;
            const pointsDiff = Math.max(0, optionPoints - pointsAvailable);
            const isFeatured = option.rentCreditOptionId === bestValueId;
            const dollars = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(Number(option.amount));

            return (
              <button
                key={option.rentCreditOptionId}
                type='button'
                className={`rd-rc${isFeatured ? ' rd-rc-featured' : ''}${eligible ? ' rd-rc-eligible' : ''}`}
                disabled={!eligible || isRedeeming}
                onClick={() => void onRedeem(option)}
                aria-busy={isRedeeming}
              >
                <div className='rd-rc-top'>
                  <div className='rd-rc-chip'>
                    <span className='rd-rc-chip-dot' />
                    <span className='rd-rc-chip-txt'>Rent savings</span>
                  </div>
                  {isFeatured ? (
                    <span className='rd-rc-best-val'>Best value</span>
                  ) : eligible ? (
                    <span className='rd-rc-unlock-badge'>
                      {isRedeeming ? 'Redeeming…' : 'Redeem'}
                    </span>
                  ) : (
                    <span className='rd-rc-lock' aria-hidden='true'>
                      🔒
                    </span>
                  )}
                </div>

                <div className='rd-rc-left'>
                  <div className='rd-rc-amt'>{dollars} off rent</div>
                </div>

                <div className='bottom'>
                  <div className='rd-rc-unlock'>Unlock with</div>
                  <div className='rd-rc-pts'>{formatPoints(optionPoints)} pts</div>

                  {eligible ? (
                    <div className='rd-rc-need'>Ready to redeem</div>
                  ) : (
                    <div className='rd-rc-need'>
                      Need <b>{formatPoints(pointsDiff)}</b> more
                    </div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </section>
  );
};
